// src/data/weatherService.js
// Uses Open-Meteo API (free, no API key required)
// https://open-meteo.com/

const _weatherFallback = {
  temperature: 0,
  humidity: null,
  windSpeed: null,
  description: '',
  icon: null,
  visibility: null,
  pressure: null,
  sunrise: null,
  sunset: null,
  cloudCover: null,
  location: null
};

/**
 * WMO Weather Codes mapping
 * https://www.nodc.noaa.gov/gcos/wmo-codes/
 */
const WMO_CODES = {
  0: 'Clear sky',
  1: 'Mainly clear',
  2: 'Partly cloudy',
  3: 'Overcast',
  45: 'Foggy',
  48: 'Foggy (depositing rime)',
  51: 'Light drizzle',
  53: 'Moderate drizzle',
  55: 'Dense drizzle',
  61: 'Slight rain',
  63: 'Moderate rain',
  65: 'Heavy rain',
  71: 'Slight snow',
  73: 'Moderate snow',
  75: 'Heavy snow',
  77: 'Snow grains',
  80: 'Slight rain showers',
  81: 'Moderate rain showers',
  82: 'Violent rain showers',
  85: 'Slight snow showers',
  86: 'Heavy snow showers',
  95: 'Thunderstorm',
  96: 'Thunderstorm with slight hail',
  99: 'Thunderstorm with heavy hail'
};

const WMO_ICONS = {
  0: 'â˜€ï¸',
  1: 'ðŸŒ¤ï¸',
  2: 'â›…',
  3: 'â˜ï¸',
  45: 'ðŸŒ«ï¸',
  48: 'ðŸŒ«ï¸',
  51: 'ðŸŒ§ï¸',
  53: 'ðŸŒ§ï¸',
  55: 'ðŸŒ§ï¸',
  61: 'ðŸŒ§ï¸',
  63: 'ðŸŒ§ï¸',
  65: 'â›ˆï¸',
  71: 'ðŸŒ¨ï¸',
  73: 'ðŸŒ¨ï¸',
  75: 'ðŸŒ¨ï¸',
  77: 'ðŸŒ¨ï¸',
  80: 'ðŸŒ§ï¸',
  81: 'ðŸŒ§ï¸',
  82: 'â›ˆï¸',
  85: 'ðŸŒ¨ï¸',
  86: 'ðŸŒ¨ï¸',
  95: 'â›ˆï¸',
  96: 'â›ˆï¸',
  99: 'â›ˆï¸'
};

export class WeatherService {
  /**
   * Fetch current weather from Open-Meteo API
   * No API key required - completely free and open
   */
  static async getCurrentWeather(lat, lon) {
    try {
      const url = new URL('https://api.open-meteo.com/v1/forecast');
      const params = new URLSearchParams({
        latitude: String(lat),
        longitude: String(lon),
        current: 'temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m,wind_gusts_10m,wind_direction_10m,cloud_cover,cloud_cover_low,cloud_cover_mid,cloud_cover_high,pressure_msl,visibility',
        daily: 'sunrise,sunset',
        timezone: 'auto',
        forecast_days: '1'
      });
      url.search = params.toString();

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
return { ..._weatherFallback };
      }

      const data = await response.json();
      const current = data.current || {};
      const daily = (data.daily && data.daily.sunrise && data.daily.sunset) ? data.daily : null;

      const weatherCode = current.weather_code ?? 0;
      const description = WMO_CODES[weatherCode] || `Code ${weatherCode}`;
      const icon = WMO_ICONS[weatherCode] || 'ðŸŒ¤ï¸';

      return {
        temperature: Number.isFinite(current.temperature_2m) ? current.temperature_2m : 0,
        humidity: current.relative_humidity_2m ?? null,
        windSpeed: current.wind_speed_10m ?? null,
        description,
        icon,
        visibility: current.visibility ?? null,
        pressure: current.pressure_msl ?? null,
        sunrise: daily?.sunrise?.[0] ? new Date(daily.sunrise[0]) : null,
        sunset: daily?.sunset?.[0] ? new Date(daily.sunset[0]) : null,
        cloudCover: current.cloud_cover ?? null,
        location: data.timezone || null
      };
    } catch (error) {
return { ..._weatherFallback };
    }
  }

  static async getWeatherForecast(lat, lon) {
    return [];
  }
}

